import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenu(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16" aria-label="主导航">
        <Link to="/" className="flex items-center gap-2 group">
          <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
            <i className="ri-play-fill text-background-50 text-lg"></i>
          </div>
          <span className={`font-heading text-xl transition-colors duration-200 ${
            scrolled || !isHome ? 'text-foreground-900' : 'text-white'
          }`}>
            黄瓜视频
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          <Link
            to="/"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
              location.pathname === '/'
                ? 'bg-primary-100 text-primary-700'
                : scrolled || !isHome
                  ? 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
                  : 'text-white/90 hover:text-white hover:bg-white/10'
            }`}
          >
            首页
          </Link>

          <div className="relative" onMouseEnter={() => setCategoryOpen(true)} onMouseLeave={() => setCategoryOpen(false)}>
            <button
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap flex items-center gap-1 ${
                location.pathname.startsWith('/category')
                  ? 'bg-primary-100 text-primary-700'
                  : scrolled || !isHome
                    ? 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              分类浏览
              <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${categoryOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {categoryOpen && (
              <div className="absolute top-full left-0 mt-1 w-56 bg-background-50 rounded-xl border border-background-200/70 py-2 animate-[fadeIn_0.15s_ease-out]">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors duration-150"
                  >
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${cat.icon} text-base`}></i>
                    </span>
                    <span>{cat.name}</span>
                    <span className="ml-auto text-xs text-foreground-400">{cat.count.toLocaleString()}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/about"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
              location.pathname === '/about'
                ? 'bg-primary-100 text-primary-700'
                : scrolled || !isHome
                  ? 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
                  : 'text-white/90 hover:text-white hover:bg-white/10'
            }`}
          >
            关于我们
          </Link>

          <a
            href="#"
            className="ml-2 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
          >
            下载APP
          </a>
        </div>

        <button
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg ${
            scrolled || !isHome ? 'text-foreground-700' : 'text-white'
          }`}
          onClick={() => setMobileMenu(!mobileMenu)}
          aria-label="菜单"
        >
          <i className={`text-xl ${mobileMenu ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </nav>

      {mobileMenu && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 animate-[fadeIn_0.2s_ease-out]">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              className={`block px-4 py-3 rounded-lg text-sm font-medium ${
                location.pathname === '/'
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              首页
            </Link>
            <div className="px-4 py-2 text-xs font-medium text-foreground-400 uppercase tracking-wide">视频分类</div>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm ${
                  location.pathname === `/category/${cat.slug}`
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <span className="w-6 h-6 flex items-center justify-center">
                  <i className={`${cat.icon} text-base`}></i>
                </span>
                <span>{cat.name}</span>
              </Link>
            ))}
            <Link
              to="/about"
              className={`block px-4 py-3 rounded-lg text-sm font-medium ${
                location.pathname === '/about'
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              关于我们
            </Link>
            <a
              href="#"
              className="block text-center mt-3 mx-4 px-5 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              下载APP
            </a>
          </div>
        </div>
      )}
    </header>
  );
}